import { fetchUtils } from '$utils/fetchUtils'
import { mmpvState } from '$lib/mmpv/mmpv.state.svelte'
import type {
	MaterialProperty,
	MaterialPropertyListElement
} from '$lib/mmpv/types/materialProperty'
import type { Material } from '$lib/mmpv/types/material'
import { DefaultNotification, NotificationType } from '$types/notification'
import { addNotification } from '$lib/stores'
import { get } from 'svelte/store'
import { t } from '$i18n/i18n'

export async function fetchProperties() {
	await fetchUtils
		.get('/api/material-properties')
		.then((data) => {
			mmpvState.materialPropertyParents.clear()
			;(data ?? []).forEach((prop: MaterialProperty) => {
				mmpvState.materialProperties.set(prop.id, prop)
				writePropertyParents(prop)
			})
		})
		.catch(fetchUtils.catchErrorAndShowNotification())
}

/**
 * Writes the property as parent into the store for all the subproperties in the contained listElements.
 * Goes through each property list element and each property child ID on those list elements,
 * checks if it exists in the material property parents then either adds it to the existing list or creates a new one.
 * @param {MaterialProperty} property - The material property for which parents need to be searched.
 */ function writePropertyParents(property: MaterialProperty) {
	property.propertyListElements.forEach((listElement: MaterialPropertyListElement) => {
		listElement.propertyChildIds.forEach((propertyId: string) => {
			if (mmpvState.materialPropertyParents.has(propertyId)) {
				// add the rest of the existing list
				mmpvState.materialPropertyParents.set(propertyId, [
					...(mmpvState.materialPropertyParents.get(propertyId) || []),
					property.id
				])
			} else {
				// create a new list and set it
				mmpvState.materialPropertyParents.set(propertyId, [property.id])
			}
		})
	})
}

export function updateNodePropertiesByDeletingProperty(propertyId: string) {
	// remove property or propertyId of deleted property in all nodes
	if (propertyId) {
		mmpvState.catalogs.forEach((catalog) => {
			catalog.nodes.forEach((node: Material) => {
				if (node.properties !== undefined) {
					const indexOfProperty = node.properties
						.map((node: MaterialProperty) => node.id)
						.indexOf(propertyId)
					if (indexOfProperty > -1) {
						node.properties.splice(indexOfProperty, 1)
					}
					const indexOfPropertyId = node.propertyIds.indexOf(propertyId)
					if (indexOfPropertyId > -1) {
						node.propertyIds.splice(indexOfPropertyId, 1)
					}
				}
			})
		})
	}
}

export async function deleteProperty(
	property: MaterialProperty,
	onUpdateProperties?: (propertyId: string) => void
): Promise<void> {
	return fetchUtils
		.post(`/api/property/remove`, property)
		.then(() => {
			mmpvState.deleteMaterialProperty(property.id)
			// remove the deleted property's own parents entry
			mmpvState.materialPropertyParents.delete(property.id)
			// remove the deleted property's ID from every remaining property's parent references
			mmpvState.materialPropertyParents.forEach((parents, key) => {
				const filtered = parents.filter((id) => id !== property.id)
				if (filtered.length !== parents.length) {
					mmpvState.materialPropertyParents.set(key, filtered)
				}
			})
			updateNodePropertiesByDeletingProperty(property.id)
			// remove propertyId from propertyChildIds of all list elements
			mmpvState.materialProperties.forEach((storeProperty: MaterialProperty) => {
				if (storeProperty.propertyListElements) {
					storeProperty.propertyListElements.forEach(
						(propertyListElement: MaterialPropertyListElement) => {
							if (propertyListElement.propertyChildIds) {
								const indexOfRemovedPropertyId = propertyListElement.propertyChildIds.indexOf(
									property.id
								)
								if (indexOfRemovedPropertyId > -1) {
									propertyListElement.propertyChildIds.splice(indexOfRemovedPropertyId, 1)
								}
							}
						}
					)
				}
			})
			onUpdateProperties?.(property.id)
			addNotification(
				DefaultNotification(
					get(t)('UI.mmpv.materialProperty.deleted.notification.success'),
					NotificationType.SUCCESS
				)
			)
		})
		.catch(fetchUtils.catchErrorAndShowNotification())
}

/** Sorts by priority (properties without a priority go last), ties broken by name. */
export function getSortedPropertiesOfMaterial(material: Material | undefined): MaterialProperty[] {
	if (!material?.properties) return []

	// oxlint-disable-next-line e18e/prefer-array-to-sorted
	return [...material?.properties ?? []].sort(
		(a, b) =>
			(a.priority ?? Number.MAX_SAFE_INTEGER) - (b.priority ?? Number.MAX_SAFE_INTEGER) ||
			(a.name ?? '').localeCompare(b.name ?? '')
	)
}
